/**
 * MailChimp Syncs Component
 * Manages member sync statuses with MailChimp
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { mailchimpService } from '../../services/mailchimpService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import './MailChimpSyncs.css';

const MailChimpSyncs = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();
  
  // State management
  const [syncs, setSyncs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedSyncs, setSelectedSyncs] = useState([]);
  
  // Filters
  const [filters] = useState({
    sync_status: '',
    search: '',
    ordering: '-last_sync_at'
  });
  
  // Statistics
  const [stats, setStats] = useState({
    total_members: 0,
    synced: 0,
    pending: 0,
    failed: 0,
    subscribed: 0
  });

  // Load syncs
  const loadSyncs = useCallback(async (isSearch = false) => {
    try {
      if (isSearch) {
        setIsProcessing(true);
      } else {
        setIsLoading(true);
      }

      const response = await mailchimpService.getMailChimpSyncs(filters);
      
      if (response.success) {
        const syncsData = response.data || [];
        setSyncs(syncsData);
        
        // Update statistics if not a search
        if (!isSearch) {
          updateStatistics(syncsData);
        }
      } else {
        showError(response.message || 'Failed to load sync statuses', 'Error');
      }
    } catch (error) {
      showError('Failed to load sync statuses', 'Error');
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  }, [filters, showError]);

  // Load sync status overview
  const loadSyncStatus = useCallback(async () => {
    try {
      const response = await mailchimpService.getMailChimpDashboard();
      
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      // Silently fail for status overview
    }
  }, []);

  // Update statistics from sync data
  const updateStatistics = (syncsData) => {
    const stats = {
      total_members: syncsData.length,
      synced: syncsData.filter(sync => sync.sync_status === 'synced').length,
      pending: syncsData.filter(sync => sync.sync_status === 'pending').length,
      failed: syncsData.filter(sync => sync.sync_status === 'failed').length,
      subscribed: syncsData.filter(sync => sync.sync_status === 'synced').length
    };
    setStats(stats);
  };

  // Load data on component mount
  useEffect(() => {
    loadSyncs();
    loadSyncStatus();
  }, [loadSyncs, loadSyncStatus]);

  // Load syncs when filters change
  useEffect(() => {
    if (filters.search) {
      const timeoutId = setTimeout(() => {
        loadSyncs(true);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      loadSyncs(true);
    }
  }, [filters, loadSyncs]);

  // Handle filter changes

  // Handle sync member
  const handleSyncMember = async (sync) => {
    setIsProcessing(true);
    try {
      // Use member_id from the sync object, fallback to sync.member if needed
      const memberId = sync.member_id || sync.member || sync.id;
      const response = await mailchimpService.syncMember(memberId);
      
      if (response.success) {
        showSuccess('Member synced successfully', 'Success');
        loadSyncs();
      } else {
        showError(response.message || 'Sync failed', 'Error');
      }
    } catch (error) {
      showError('Sync failed', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle sync selection
  const handleSyncSelect = (syncId, isSelected) => {
    setSelectedSyncs(prev => 
      isSelected 
        ? [...prev, syncId]
        : prev.filter(id => id !== syncId)
    );
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedSyncs(syncs.map(sync => sync.id));
    } else {
      setSelectedSyncs([]);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedSyncs([]);
  };

  if (isLoading) {
    return (
      <div className="mailchimp-syncs">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="mailchimp-syncs">
      {/* Header */}
      <div className="syncs-header">
        <div className="header-content">
          <h2>🔄 Member Sync Status</h2>
          <p>Monitor and manage member synchronization with MailChimp</p>
        </div>
      </div>

      {/* Statistics */}
      <div className="syncs-stats">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Members</h3>
            <p className="stat-value">{stats.total_members}</p>
          </div>
        </div>
        <div className="stat-card synced">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Synced</h3>
            <p className="stat-value">{stats.synced}</p>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Pending</h3>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="stat-card failed">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <h3>Failed</h3>
            <p className="stat-value">{stats.failed}</p>
          </div>
        </div>
        <div className="stat-card subscribed">
          <div className="stat-icon">📧</div>
          <div className="stat-content">
            <h3>Subscribed</h3>
            <p className="stat-value">{stats.subscribed}</p>
          </div>
        </div>
      </div>

      {/* Filters - Component removed */}

      {/* Bulk Actions */}
      {selectedSyncs.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-actions-content">
            <span className="selected-count">
              {selectedSyncs.length} sync(s) selected
            </span>
            <div className="bulk-controls">
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Implement bulk sync
                  showSuccess('Bulk sync functionality coming soon', 'Info');
                }}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Sync Selected'}
              </button>
              <button
                className="btn btn-outline"
                onClick={clearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Syncs List */}
      <div className="syncs-content">
        <div className="syncs-table">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedSyncs.length === syncs.length && syncs.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Member</th>
                <th>List</th>
                <th>Status</th>
                <th>Last Sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {syncs.map(sync => (
                <tr key={sync.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSyncs.includes(sync.id)}
                      onChange={(e) => handleSyncSelect(sync.id, e.target.checked)}
                    />
                  </td>
                  <td>{sync.member_name}</td>
                  <td>{sync.list_name}</td>
                  <td>
                    <span className={`status-badge ${sync.sync_status}`}>
                      {sync.sync_status_display}
                    </span>
                  </td>
                  <td>
                    {sync.last_sync_at ? 
                      new Date(sync.last_sync_at).toLocaleDateString() : 
                      'Never'
                    }
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleSyncMember(sync)}
                      disabled={!canManageIntegrations || isProcessing}
                    >
                      Sync
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {syncs.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🔄</div>
          <h3>No Sync Data</h3>
          <p>No member sync data available. Members need to be synced with MailChimp first.</p>
        </div>
      )}
    </div>
  );
};

export default MailChimpSyncs;
